import { Children, cloneElement, FC, ReactElement, ReactNode } from 'react'
import { ExclamationCircleIcon } from '@heroicons/react/outline'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/solid'
import {
  DragDropContext,
  DragDropContextProps,
  Droppable,
  DroppableProps,
} from 'react-beautiful-dnd'
import { Button } from './Button'
import pluralize from 'pluralize'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import type { PaginationMeta } from '@trybeapp/sdk'
import { noop } from 'lodash'
import { render } from 'utilities/Function'
import type { LegacySdkResponseError } from 'api/api.types'
import { usePageQueryParam } from 'hooks/UsePageQueryParam'
import { UseQueryResult } from 'react-query'

interface DraggablePaginatedMiniListProps
  extends Pick<DragDropWrapperProps, 'droppableId' | 'onDragEnd'> {
  draggable: true
}

interface StaticPaginatedMiniListProps
  extends Partial<Pick<DragDropWrapperProps, 'droppableId' | 'onDragEnd'>> {
  draggable?: false
}

type PaginatedMiniListProps = ListProps &
  (DraggablePaginatedMiniListProps | StaticPaginatedMiniListProps)

export const PaginatedMiniList: FC<PaginatedMiniListProps> = ({
  isLoading = false,
  isError = false,
  isFetching = false,
  error,
  rows = [],
  meta = null,
  onPageChange = noop,
  emptyMessage,
  borderless = false,
  hidePaginationIfSinglePage = false,
  draggable = false,
  droppableId = 'mini-list',
  onDragEnd,
}) => (
  <DragDropWrapper
    draggable={draggable}
    onDragEnd={onDragEnd as DragDropWrapperProps['onDragEnd']}
    droppableId={droppableId}
  >
    <List
      isLoading={isLoading}
      isError={isError}
      isFetching={isFetching}
      error={error}
      rows={rows}
      meta={meta}
      onPageChange={onPageChange}
      emptyMessage={emptyMessage}
      borderless={borderless}
      hidePaginationIfSinglePage={hidePaginationIfSinglePage}
    />
  </DragDropWrapper>
)

interface DragDropWrapperProps
  extends Pick<DragDropContextProps, 'onDragEnd'>,
    Pick<DroppableProps, 'droppableId'> {
  children: ReactElement
  draggable?: boolean
}

const DragDropWrapper: FC<DragDropWrapperProps> = ({
  children,
  draggable,
  onDragEnd,
  droppableId,
}) => {
  if (draggable) {
    return (
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable
          droppableId={droppableId}
          renderClone={(provided, snapshot, rubric) =>
            // The clone is rendered by the `rows` prop of the
            // PaginatedMiniList component. We can determine which
            // element is being dragged by looking at the `rubric` source index.
            children.props.rows[rubric.source.index].props.children(provided, snapshot)
          }
        >
          {(provided) => {
            return (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {Children.map(children, (child) =>
                  cloneElement(child, { placeholder: provided.placeholder })
                )}
              </div>
            )
          }}
        </Droppable>
      </DragDropContext>
    )
  }

  return children
}

interface ListProps extends Partial<Pick<UseQueryResult, 'isError' | 'isFetching' | 'isLoading'>> {
  /** The contents of the error returned. This includes the status code */
  error?: Pick<LegacySdkResponseError, 'status'> | null
  /** An array of components to render for each row */
  rows?: ReactNode[]
  /** An object containing pagination info */
  meta?: Pick<PaginationMeta, 'current_page' | 'last_page' | 'total'> | null
  /** Callback for when the page is changed */
  onPageChange?: (page: number) => void
  /** A message to display when the list is empty */
  emptyMessage?: string
  /** Whether to display a 'lighter weight' version of the list without borders */
  borderless?: boolean
  /** Whether to hide the pagination options if there's only one page of results */
  hidePaginationIfSinglePage?: boolean
}

const List: FC<ListProps> = ({
  isLoading,
  isError,
  isFetching,
  error,
  rows = [],
  meta,
  onPageChange,
  emptyMessage,
  borderless,
  hidePaginationIfSinglePage,
}) => {
  const { t } = useTranslation()
  const [page] = usePageQueryParam()
  const isEmpty = !isLoading && !isError && rows.length === 0

  return (
    <ul
      className={classNames(
        'text-sm overflow-hidden border-border rounded-lg divide-y divide-border',
        {
          'flex border-2 border-dashed min-h-[150px]': (isError || isEmpty) && !borderless,
          'bg-background': !(isError || isEmpty),
          'shadow-sm border': !(isError || isEmpty) && !borderless,
        }
      )}
    >
      {render(() => {
        if (isLoading) {
          return (
            <>
              <LoadingRow />
              <LoadingRow />
              <LoadingRow />
              <LoadingRow />
              <LoadingRow />
            </>
          )
        }

        if (isError) {
          return error?.status === 403 ? (
            <div className="self-center text-center w-full text-lg font-medium text-gray-600">
              <ExclamationCircleIcon className="w-8 h-8 mb-2 mx-auto" aria-hidden="true" />
              {t('mini_list.missing_permissions_error')}
            </div>
          ) : (
            <div className="self-center text-center w-full text-lg font-medium text-gray-600">
              <ExclamationCircleIcon className="w-8 h-8 mb-2 mx-auto" aria-hidden="true" />
              {t('mini_list.error')}
            </div>
          )
        }

        if (rows.length === 0) {
          return (
            <div className="self-center text-center w-full text-lg font-medium text-gray-600">
              {emptyMessage}
            </div>
          )
        }

        return rows
      })}

      {!isEmpty &&
        !isError &&
        !isLoading &&
        meta &&
        (meta.last_page > 1 || !hidePaginationIfSinglePage) && (
          <div
            className={classNames(
              ' text-gray-600 dark:text-gray-400 text-xs p-4 py-3 flex justify-between items-center',
              {
                'bg-gray-50 dark:bg-transparent': !borderless,
              }
            )}
          >
            <span>
              {t('paginated_mini_list.meta', {
                total: meta.total,
                results: pluralize('result', meta.total),
                currentPage: meta.current_page,
                totalPages: meta.last_page,
              })}
            </span>

            <div className="flex space-x-2">
              <Button
                size="xs"
                disabled={meta.current_page <= 1}
                leadingIcon={ChevronLeftIcon}
                label={t('paginated_mini_list.button.previous')}
                loading={isFetching && page < meta.current_page}
                hideLabel
                onClick={() => onPageChange?.(meta.current_page - 1)}
              />

              <Button
                size="xs"
                disabled={meta.current_page >= meta.last_page}
                leadingIcon={ChevronRightIcon}
                label={t('paginated_mini_list.button.next')}
                loading={isFetching && page > meta.current_page}
                hideLabel
                onClick={() => onPageChange?.(meta.current_page + 1)}
              />
            </div>
          </div>
        )}
    </ul>
  )
}

const LoadingRow: FC = () => (
  <div className="p-4 animate-pulse">
    <div className="w-32 h-5 bg-gray-100" />
    <div className="w-3/4 h-4 mt-3 bg-gray-100" />
  </div>
)
